import { App, Form, Input, InputNumber, Modal, Radio } from 'antd';
import { useRef, useState } from 'react';
import { bulkCreateTables, createTable, updateTable } from '../api';
import {
  AdminTable,
  BULK_TABLES_MAX,
  GalaTableShape,
  TABLE_CAPACITY_DEFAULT,
  TABLE_CAPACITY_MAX,
  TABLE_SHAPE_META,
  errorText,
} from '../contract';

const { TextArea } = Input;

/** Lỗi sửa / tạo bàn; giảm số ghế mà ghế cần bỏ đang có người thì ghép số ghế vướng vào câu. */
export function tableErrorText(e: any, fallback: string): string {
  const text = errorText(e, fallback);
  if (e?.code === 'GALA_TABLE_SHRINK_OCCUPIED' && Array.isArray(e?.seats) && e.seats.length) {
    return `${text} Ghế vướng: ${e.seats.join(', ')}.`;
  }
  return text;
}

export type TableModalMode = 'create' | 'edit' | 'bulk';

interface Props {
  eventId: number;
  /** null = đóng. */
  mode: TableModalMode | null;
  /** Bàn đang sửa (mode 'edit'). */
  table?: AdminTable | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

interface TableValues {
  name: string;
  shape: GalaTableShape;
  capacity: number;
  note: string;
}

interface BulkValues {
  count: number;
  prefix: string;
}

const SHAPES = Object.keys(TABLE_SHAPE_META) as GalaTableShape[];

/** Thêm bàn / Sửa bàn / Thêm nhanh N bàn tròn 10 ghế (SPEC mục 7). Enter để lưu. */
const TableModal = ({ eventId, mode, table, onClose, onSaved }: Props) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<TableValues>();
  const [bulkForm] = Form.useForm<BulkValues>();
  const [saving, setSaving] = useState(false);
  // Đã tự sửa Số ghế thì đổi Hình không ghi đè nữa.
  const capacityTouched = useRef(false);

  const isBulk = mode === 'bulk';
  const isEdit = mode === 'edit' && !!table;

  const submit = async () => {
    if (saving || !mode) return;
    setSaving(true);
    try {
      if (isBulk) {
        const v = await bulkForm.validateFields();
        const made = await bulkCreateTables(eventId, {
          count: v.count,
          prefix: v.prefix?.trim() || 'Bàn',
        });
        message.success(`Đã thêm ${made.length} bàn.`);
      } else {
        const v = await form.validateFields();
        const input = {
          name: v.name.trim(),
          shape: v.shape,
          capacity: v.capacity,
          note: v.note?.trim() ? v.note.trim() : null,
        };
        if (isEdit && table) {
          await updateTable(eventId, table.id, input);
          message.success('Đã lưu bàn.');
        } else {
          await createTable(eventId, input);
          message.success(`Đã thêm ${input.name}.`);
        }
      }
      await onSaved();
      onClose();
    } catch (e: any) {
      // Lỗi kiểm form của antd đã hiện dưới ô nhập.
      if (!e?.errorFields) {
        message.error({
          content: tableErrorText(e, isBulk ? 'Không thêm được bàn.' : 'Không lưu được bàn.'),
          duration: 6,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const title = isBulk ? 'Thêm nhanh bàn tròn' : isEdit ? `Sửa bàn — ${table?.name}` : 'Thêm bàn';

  return (
    <Modal
      title={title}
      open={!!mode}
      onCancel={onClose}
      onOk={submit}
      okText={isBulk ? 'Thêm' : 'Lưu'}
      cancelText="Huỷ"
      confirmLoading={saving}
      destroyOnHidden
      afterOpenChange={(open) => {
        if (open) capacityTouched.current = false;
      }}
    >
      {isBulk ? (
        <Form
          form={bulkForm}
          layout="vertical"
          preserve={false}
          initialValues={{ count: 10, prefix: 'Bàn' }}
        >
          <Form.Item
            label="Số bàn tròn 10 ghế"
            name="count"
            rules={[{ required: true, message: 'Nhập số bàn' }]}
            extra={`Tạo "Bàn 1", "Bàn 2"… tiếp theo số lớn nhất đang có; sau đó bấm Sửa để đổi tên từng bàn.`}
          >
            <InputNumber
              min={1}
              max={BULK_TABLES_MAX}
              precision={0}
              autoFocus
              style={{ width: 160 }}
              onPressEnter={submit}
            />
          </Form.Item>
          <Form.Item
            label="Tên bắt đầu bằng"
            name="prefix"
            rules={[{ max: 40, message: 'Tối đa 40 ký tự' }]}
          >
            <Input placeholder="Bàn" onPressEnter={submit} />
          </Form.Item>
        </Form>
      ) : (
        <Form
          key={table?.id ?? 'new'}
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={{
            name: isEdit ? table?.name : '',
            shape: isEdit ? table?.shape : 'ROUND',
            capacity: isEdit ? table?.capacity : TABLE_CAPACITY_DEFAULT.ROUND,
            note: (isEdit && table?.note) || '',
          }}
          onValuesChange={(changed: Partial<TableValues>) => {
            if ('capacity' in changed) capacityTouched.current = true;
            // Chỉ khi thêm bàn mới: sửa bàn đang có thì giữ nguyên số ghế (tránh lỡ tay bỏ ghế có người).
            if (changed.shape && !isEdit && !capacityTouched.current) {
              form.setFieldValue('capacity', TABLE_CAPACITY_DEFAULT[changed.shape]);
            }
          }}
        >
          <Form.Item
            label="Tên bàn"
            name="name"
            rules={[
              { required: true, whitespace: true, message: 'Nhập tên bàn' },
              { max: 60, message: 'Tối đa 60 ký tự' },
            ]}
          >
            <Input autoFocus placeholder="VD: Team HT" onPressEnter={submit} />
          </Form.Item>
          <Form.Item label="Hình" name="shape">
            <Radio.Group
              optionType="button"
              options={SHAPES.map((s) => ({ value: s, label: TABLE_SHAPE_META[s].text }))}
            />
          </Form.Item>
          <Form.Item
            label="Số ghế"
            name="capacity"
            rules={[{ required: true, message: 'Nhập số ghế' }]}
            extra={
              isEdit
                ? 'Giảm số ghế chỉ được khi các ghế bị bỏ (số lớn nhất) đang trống.'
                : `Bàn tròn chuẩn ${TABLE_CAPACITY_DEFAULT.ROUND} ghế, bàn dài ${TABLE_CAPACITY_DEFAULT.LONG} ghế.`
            }
          >
            <InputNumber
              min={1}
              max={TABLE_CAPACITY_MAX}
              precision={0}
              style={{ width: 160 }}
              onPressEnter={submit}
            />
          </Form.Item>
          <Form.Item
            label="Ghi chú"
            name="note"
            rules={[{ max: 200, message: 'Tối đa 200 ký tự' }]}
          >
            <TextArea rows={2} placeholder="Chỉ hiện trong trang quản trị" />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default TableModal;
